import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { Centrifuge, Subscription, PublicationContext } from 'centrifuge';
import { HttpClient } from '@/api/services/HttpClient';
import { useAuth } from '@/context/AuthContext';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useSelector } from 'react-redux';
import { addNewTicket, checkIfMessageIsForActiveTicket, updateTicketDataSync } from '@/store/slices/tickets/ticketsSlice';
import { addNewTicketToViews, bumpTicketByIdToTopOfViews } from '@/store/slices/ticketPages/ticketPagesSlice';
import { keepUserActive, selectAgentStatuses, selectUserCurrentStatus } from '@/store/slices/agentStatuses/agentStatusesSlice';
import { updateConversationStatus, updateReadState } from '@/store/slices/conversations/conversationsSlice';
import { updateCustomer } from '@/store/slices/customers/customersSlice';

interface CentrifugoContextValue {
  client: Centrifuge | null;
  connected: boolean;
  subscribe: (channel: string, handler: (ctx: PublicationContext) => void) => Subscription;
  unsubscribe: (channel: string) => void;
}

const CentrifugoContext = createContext<CentrifugoContextValue | null>(null);

export const useCentrifugo = () => {
  const ctx = useContext(CentrifugoContext);
  if (!ctx) throw new Error('useCentrifugo must be used within CentrifugoProvider');
  return ctx;
};

interface CentrifugoProviderProps {
  children: ReactNode;
}

export const CentrifugoProvider: React.FC<CentrifugoProviderProps> = ({ children }) => {
  const clientRef = useRef<Centrifuge | null>(null);
  const subsRef = useRef<Map<string, Subscription>>(new Map());
  const [connected, setConnected] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const { sessionData: user } = useAuth();
  const dispatch = useAppDispatch();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isUserLoggedIn = Boolean(user);

  const userActivityInterval = useRef<NodeJS.Timeout | null>(null);
  const currentUserAgentStatus = useSelector(selectUserCurrentStatus);
  const agentStatuses = useSelector(selectAgentStatuses);

  useEffect(() => {
    audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
    audioRef.current.preload = 'auto';
  }, []);

  // Keep pinging the server to keep the user active
  useEffect(() => {
    if (isUserLoggedIn) {
      userActivityInterval.current = setInterval(() => {
        if (currentUserAgentStatus) {
          const status = agentStatuses.find(s => s.id === currentUserAgentStatus);
          if (status?.isActiveStatus) {
            void dispatch(keepUserActive());
          }
        }
      }, 60000);

      return () => {
        if (userActivityInterval.current) {
          clearInterval(userActivityInterval.current);
        }
      };
    }
  }, [currentUserAgentStatus, agentStatuses]);

  // --- Notification handlers ---

  const handleTicketMessage = (data: any) => {
    const currentUserId = user?.user?.internalUserId as string;
    const ticketId = data.ticketId || data.id;

    if (!ticketId) return;

    // Merge lastMessage into the existing ticket — updateTicketDataSync does a shallow merge
    dispatch(updateTicketDataSync({
      id: ticketId,
      lastMessage: data.message || data.content || '',
      showMessageBadge: true,
    }));

    dispatch(bumpTicketByIdToTopOfViews(ticketId, currentUserId));

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.warn('Failed to play notification sound:', err);
      });
    }

    dispatch(checkIfMessageIsForActiveTicket({ ticketId, callback: () => {} }));
  };

  const handleNewTicket = (data: any) => {
    const ticket = data;
    const currentUserId = user?.user?.internalUserId as string;
    dispatch(addNewTicket(ticket));
    dispatch(addNewTicketToViews({ ticket, currentUserId }));
  };

  const handleCustomerDataUpdate = (data: any) => {
    if (data && Object.keys(data).length > 0) {
      dispatch(updateCustomer(data));
    }
  };

  const handleTicketDataUpdate = (data: any) => {
    dispatch(updateTicketDataSync(data));
  };

  // Handles delivery_status events on notifications:{userId}.
  // "sent" — backend confirmed agent's message was saved (updates optimistic message status).
  // "read" — customer read the conversation (updates ticket-level readState).
  const handleMessageReceipt = (data: any) => {
    const { clientGeneratedId, dashboardGeneratedId, ticketId, status } = data;

    if (status === 'read' && data.readAt) {
      // Customer read receipt — update ticket-level read state
      dispatch(updateReadState({
        ticketId,
        customerReadAt: data.readAt,
      }));
      return;
    }

    // "sent" confirmation for optimistic messages
    dispatch(updateConversationStatus({
      ticketId,
      dashboardGeneratedId: clientGeneratedId ?? dashboardGeneratedId ?? null,
      status,
      id: data.id ?? null,
    }));
  };

  // --- Connect to Centrifugo ---

  useEffect(() => {
    if (!isUserLoggedIn) return;

    const centrifuge = new Centrifuge(
      import.meta.env.VITE_CENTRIFUGO_WS_URL || 'ws://localhost:8800/connection/websocket',
      {
        getToken: async () => {
          const response = await HttpClient.apiClient.get('/realtime/agent-token');
          return response.data.data.token;
        },
      }
    );

    centrifuge.on('connected', () => {
      console.log('[Centrifugo] Connected');
      setConnected(true);
    });

    centrifuge.on('disconnected', (ctx) => {
      console.log('[Centrifugo] Disconnected:', ctx.reason);
      setConnected(false);
    });

    centrifuge.connect();
    clientRef.current = centrifuge;
    setInitialized(true);

    return () => {
      centrifuge.disconnect();
      subsRef.current.forEach((sub) => {
        sub.unsubscribe();
        sub.removeAllListeners();
        centrifuge.removeSubscription(sub);
      });
      subsRef.current.clear();
      clientRef.current = null;
      setInitialized(false);
      setConnected(false);
    };
  }, [isUserLoggedIn]);

  // --- Subscribe to default channels once connected ---

  useEffect(() => {
    if (!connected || !clientRef.current || !user?.user?.id || !user?.user?.clientId) return;

    const userId = user.user.id;
    const clientId = user.user.clientId;

    // notifications:client:{clientId} — new_ticket, ticket_message, ticket_update
    subscribe(`notifications:client:${clientId}`, (ctx) => {
      const data = ctx.data;
      console.log(`[Centrifugo] notifications:client:${clientId} →`, data.event, data);
      switch (data.event) {
        case 'new_ticket':
          handleNewTicket(data.payload ?? data);
          break;
        case 'ticket_message':
          handleTicketMessage(data.payload ?? data);
          break;
        case 'ticket_update':
          if (data.payload) {
            // Updates can contain only the changed fields (for example, assignment).
            dispatch(updateTicketDataSync(data.payload));
          }
          break;
        case 'delivery_status':
          handleMessageReceipt(data);
          break;
      }
    });

    // notifications:{userId} — delivery_status, new_message, new_assignment
    subscribe(`notifications:${userId}`, (ctx) => {
      const data = ctx.data;
      console.log(`[Centrifugo] notifications:${userId} →`, data.event, data);
      switch (data.event) {
        case 'delivery_status':
          handleMessageReceipt(data);
          break;
        case 'new_message':
          // handled by ticket subscription in ConversationPanel
          break;
        case 'new_assignment':
          break;
      }
    });

    // data-sync:user:{userId} — customer_data_update, ticket_data_update
    subscribe(`data-sync:user:${userId}`, (ctx) => {
      const data = ctx.data;
      console.log(`[Centrifugo] data-sync:user:${userId} →`, data.event, data);
      switch (data.event) {
        case 'customer_data_update':
          handleCustomerDataUpdate(data.payload ?? data);
          break;
        case 'ticket_data_update':
          handleTicketDataUpdate(data.payload ?? data);
          break;
      }
    });

    return () => {
      unsubscribe(`notifications:client:${clientId}`);
      unsubscribe(`notifications:${userId}`);
      unsubscribe(`data-sync:user:${userId}`);
    };
  }, [connected, user?.user?.id, user?.user?.clientId]);

  // --- Subscribe / Unsubscribe helpers ---

  const subscribe = (channel: string, handler: (ctx: PublicationContext) => void): Subscription => {
    if (subsRef.current.has(channel)) {
      return subsRef.current.get(channel)!;
    }

    const sub = clientRef.current!.newSubscription(channel);

    sub.on('publication', (pubCtx) => {
      console.log(`[Centrifugo] publication on ${channel} →`, pubCtx.data);
      handler(pubCtx);
    });

    sub.on('subscribed', (ctx) => {
      console.log(`[Centrifugo] Subscribed to ${channel}`,
        ctx.wasRecovering ? '(recovered)' : '(fresh)');
    });

    sub.subscribe();
    subsRef.current.set(channel, sub);
    return sub;
  };

  const unsubscribe = (channel: string) => {
    const sub = subsRef.current.get(channel);
    if (sub) {
      sub.unsubscribe();
      sub.removeAllListeners();
      clientRef.current?.removeSubscription(sub);
      subsRef.current.delete(channel);
    }
  };

  if (!initialized && isUserLoggedIn) {
    return <div>Loading...</div>;
  }

  return (
    <CentrifugoContext.Provider value={{
      client: clientRef.current,
      connected,
      subscribe,
      unsubscribe,
    }}>
      {children}
    </CentrifugoContext.Provider>
  );
};

export { CentrifugoContext };
